/** QR / link handoff for a plan (docs/SHARE_LINK_SPEC.md). House sheet-ish
 * dialog, but centered rather than the bottom-anchored .sheet the other
 * panels use — a share card reads as a discrete, momentary action, not
 * another drawer stacked on the map. App.tsx mounts this unconditionally
 * (its whole reason for owning the `share` state) and it renders nothing
 * whenever `share` is null, so it stays mounted across opens instead of
 * remounting like the other sheets — the effect below re-focuses and
 * resets the COPY/COPIED label every time a NEW share arrives, doing by
 * hand what an unmount/remount would otherwise give for free. */
import { useEffect, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import type { Plan } from "../plan/types";

/** Past this many control points a QR gets dense enough to risk not
 * scanning — same threshold docs/SHARE_LINK_SPEC.md calls out for the
 * GPX-instead nudge. */
const QR_POINT_WARNING = 200;

export default function ShareSheet({
  share,
  onClose,
}: {
  share: { plan: Plan; url: string } | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linkRef = useRef<HTMLInputElement>(null);

  // Fresh focus (for Escape to reach the card's own onKeyDown below) and a
  // reset "COPY" label every time a NEW share opens — see the doc comment
  // above for why this can't just rely on mount/unmount like other sheets.
  useEffect(() => {
    if (!share) return;
    setCopied(false);
    linkRef.current?.focus();
  }, [share]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!share) return null;
  const { plan, url } = share;

  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  const qrSvg = qr.createSvgTag({ cellSize: 4, margin: 2 });

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(false), 1000);
      },
      // Denied permission, insecure context, whatever — the link input
      // above is a select-on-focus fallback either way, so this only
      // needs to not crash. Same "log once, degrade quietly" pattern as
      // App.tsx's terrain-sampler catch.
      (e) => console.warn("clipboard write failed", e),
    );
  };

  return (
    <div className="sheet-backdrop share-backdrop" onClick={onClose}>
      <div
        className="sheet share-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-heading"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // stopPropagation load-bearing the same way PlanBar's overflow
          // menu is: ShareSheet is only reachable outside draw mode (the
          // PLAN control that leads to it is disabled mid-draw), so there
          // is no draw-mode window listener to fight today — but the
          // discipline is cheap and keeps this dialog self-contained if
          // that ever changes.
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 id="share-heading">Share route</h2>
        <div className="share-qr">
          {/* qrcode-generator's own SVG string — a QR needs a light quiet
            * zone around it to scan, hardcoded below (not var(--paledust),
            * which flips near-black at night). role="img" + aria-label on
            * the wrapper (not the generated SVG itself, which carries no
            * accessibility markup of its own) — same "QR gets a label
            * pointing at the link fallback" fix as the iOS pass's
            * ShareTripSheet. */}
          <div
            className="share-qr-tile"
            role="img"
            aria-label="QR code linking to this shared route — use the link below if it doesn't scan"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        </div>
        <div className="share-link-row">
          <input
            ref={linkRef}
            className="share-link-input"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            className="plan-bar-btn plan-bar-btn-primary share-copy-btn"
            onClick={handleCopy}
          >
            {copied ? "COPIED" : "COPY"}
          </button>
        </div>
        <p className="share-caption">
          Scan with a phone camera — opens in Dustlark, or the web planner if the app isn't
          installed.
        </p>
        {plan.points.length > QR_POINT_WARNING && (
          <p className="share-warning">Long route — the QR may not scan; use the link or GPX.</p>
        )}
      </div>
    </div>
  );
}
